import { AccordionSummary } from '@eversports/react-components/primitives/Accordion'
import Flex from '@eversports/react-components/primitives/Flex'
import Text from '@eversports/react-components/primitives/Text'
import { usePyroField } from 'pyro-form'
import React from 'react'
import { IdealBankElement } from '@stripe/react-stripe-js'
import { makeStyles } from '@mui/styles'
import { Theme } from '@eversports/react-components/design-tokens/theme'
import { StripeIdealBankElementChangeEvent } from '@stripe/stripe-js'
import { Localized } from '../../localization/react'

import useCheckoutContext from '../../data/useCheckoutContext'
import { PaymentMethodType, PaymentMethodVerificationMethod } from '../../typings/Payment'

import AdditionalPaymentMethod from './AdditionalPaymentMethod'
import PaymentMethodExpansionPanel from './PaymentMethodExpansionPanel'
import PaymentMethodExpansionPanelDetails from './PaymentMethodExpansionPanelDetails'
import PaymentMethodExpansionPanelHeader from './PaymentMethodExpansionPanelHeader'
import SepaForm from './SepaForm'

interface Props {
  isSepaIdeal: boolean
}

const useStyles = makeStyles(({ palette, borders, borderRadii }: Theme) => ({
  idealBankElement: {
    border: borders.strong,
    borderColor: palette.primary.main,
    borderRadius: borderRadii.default,
    cursor: 'pointer',
  },
}))

const IdealPaymentMethod: React.FC<React.PropsWithChildren<Props>> = ({ isSepaIdeal }) => {
  if (isSepaIdeal) {
    return <SepaIdealPaymentMethod />
  }

  return <StandaloneIdealPaymentMethod />
}

const SepaIdealPaymentMethod = () => {
  const { core } = usePyroField('type')

  return (
    <PaymentMethodExpansionPanel>
      <AccordionSummary>
        <PaymentMethodExpansionPanelHeader paymentOption="pay-with-sepa-ideal" />
      </AccordionSummary>
      <PaymentMethodExpansionPanelDetails>
        <div>
          <Flex flexWrap="wrap">
            <AdditionalPaymentMethod
              paymentMethodType={PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE}
              isSelected={core.value === PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE}
              verifyWith={PaymentMethodVerificationMethod.PAYMENT_METHOD_IDEAL}
            />
          </Flex>
          {core.value === PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE && (
            <SepaForm verifyWith={PaymentMethodVerificationMethod.PAYMENT_METHOD_IDEAL} />
          )}
        </div>
      </PaymentMethodExpansionPanelDetails>
    </PaymentMethodExpansionPanel>
  )
}

const StandaloneIdealPaymentMethod = () => {
  const { core } = usePyroField('type')
  const classes = useStyles()
  const context = useCheckoutContext()

  const handleChange = (event: StripeIdealBankElementChangeEvent) => {
    if (!event.value) return
    context.setIsPaymentValid(true)
  }

  const handleClick = () => {
    context.setIsPaymentValid(false)
  }

  return (
    <PaymentMethodExpansionPanel>
      <AccordionSummary>
        <PaymentMethodExpansionPanelHeader paymentOption="pay-with-ideal" />
      </AccordionSummary>
      <PaymentMethodExpansionPanelDetails>
        <div>
          <Flex flexWrap="wrap">
            <AdditionalPaymentMethod
              paymentMethodType={PaymentMethodType.PAYMENT_METHOD_IDEAL}
              isSelected={core.value === PaymentMethodType.PAYMENT_METHOD_IDEAL}
              verifyWith={null}
              onClick={handleClick}
            />
          </Flex>
          {core.value === PaymentMethodType.PAYMENT_METHOD_IDEAL && (
            <Flex flexDirection="column" verticalSpacing="wide">
              <Text>
                <Localized id="payment-method-information" params={{ paymentMethodType: 'Ideal' }} />
              </Text>
              <Text>
                <Localized id="ideal-payment-select-your-bank" />
              </Text>
              <div className={classes.idealBankElement}>
                <IdealBankElement
                  onReady={(el) => {
                    el.focus()
                  }}
                  onChange={handleChange}
                  options={{
                    style: {
                      base: {
                        fontFamily: "'Figtree', Arial",
                        padding: '16px',
                        fontSize: '14px',
                        color: '#333333',
                      },
                    },
                  }}
                />
              </div>
            </Flex>
          )}
        </div>
      </PaymentMethodExpansionPanelDetails>
    </PaymentMethodExpansionPanel>
  )
}

export default IdealPaymentMethod
